import { A } from '@adornis/base/env-info.js';
import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { type Observable } from 'rxjs';
import type { DuplicateDataTracker } from './DuplicateDataTracker.js';

/**
 * @entity AcquisitionRecordDuplicates
 */
@Entity()
export class AcquisitionRecordDuplicates extends AdornisEntity {
  public static override _class = 'AcquisitionRecordDuplicates';

  @Field(type => ID, {
    default: v => {
      return v ?? A.getGloballyUniqueID();
    },
  })
  public _id: Maybe<string>;

  @Field(type => Float, {
    default: v => v ?? 0,
  })
  public probabilityOfDuplicate: Maybe<number>;

  @Field(type => ID)
  public entityID: Maybe<string>;

  @Field(type => ID)
  public duplicateID: Maybe<string>;

  @Field(type => String)
  public duplicateCollection: Maybe<string>;

  @Field(type => Student, {
    resolve: function (this: DuplicateDataTracker) {
      return async () => {
        if (!this.duplicateID || !this.duplicateCollection) return null;
        const collection = await getRawCollection<EntityData<DuplicateDataTracker>>(this.duplicateCollection);
        return constructValue(await collection.findOne({ _id: this.duplicateID }));
      };
    },
  })
  public duplicate: Maybe<Student>;
}

/**
 * @entity AcquisitionRecordFilter
 */
@Entity()
export class AcquisitionRecordFilter extends AdornisFilter {
  public static override _class = 'AcquisitionRecordFilter';

  constructor(doc: PartialEntityData<AcquisitionRecordFilter>) {
    super(doc);
  }

  @Field(type => String, { default: v => v ?? null })
  public schoolID!: string | null;

  @Field(type => Boolean, { default: v => v ?? null })
  public hasStudents!: boolean | null;

  override getMongoFilter(): Dictionary<any> {
    return { $and: [{ _class: 'AcquisitionRecord' }, ...this.getFilterArray()] };
  }

  getFilterArray(): Dictionary<any>[] {
    const filter = this.toObject();

    const schoolIDFilter = filter.schoolID ? { schoolID: filter.schoolID } : {};
    const hasStudentsFilter = filter.hasStudents ? { 'createdStudents.0': { $exists: true } } : {};

    return [schoolIDFilter, hasStudentsFilter];
  }
}

/**
 * @entity AcquisitionRecord
 */
@Entity()
export class AcquisitionRecord extends MongoEntity {
  public static override _class = 'AcquisitionRecord';
  public static override _collectionName = 'acquisition-record';

  constructor(doc: PartialEntityData<AcquisitionRecord>) {
    super(doc);
  }

  @Field(type => String)
  public kpfID: Maybe<string>;

  @DataLoadedField(type => KPF, ({ kpfID }) => kpfID)
  public kpf: Maybe<KPF>;

  @Field(type => [String], {
    default: v => v ?? [],
  })
  public createdStudents?: string[];

  @Field(type => [AcquisitionRecordDuplicates], {
    default: v => v ?? [],
  })
  public duplicates: Maybe<AcquisitionRecordDuplicates[]>;

  @Query(type => AcquisitionRecord)
  static getAcquisitionRecordForKPF(@Arg('kpfID', type => String) kpfID: string) {
    return async (gqlFields: BaseQLSelectionSet<AcquisitionRecord>) => {
      if (!kpfID) return null;

      const collection = await getCollection(AcquisitionRecord._class);
      const record = (await collection.findOne({
        kpfID,
      })) as AcquisitionRecord | null;

      if (!record) {
        let newRecord = new AcquisitionRecord({
          kpfID,
        });

        const id = await newRecord.create();
        newRecord._id = id;
        return newRecord;
      }
      return record;
    };
  }

  @Subscription(type => [AcquisitionRecord])
  public static subscribeAcquisitionRecordsByFilterPaginated(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filterObject', type => AcquisitionRecordFilter) filter: AcquisitionRecordFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<AcquisitionRecord>) => {
      return AcquisitionRecord.subscribePaginated<AcquisitionRecord>(skip, limit, filter.getMongoFilter(), {
        ...filter.getMongoOptions().sort,
      }) as Observable<AcquisitionRecord[]>;
    };
  }
}
